import { FC, useState, useCallback, useRef } from 'react';
import { Title, Subtitle } from 'src/shared';
import color from 'src/theme/color';
import {
    Container,
    RichTextEditor,
    AttachmentsContainer,
    AttachmentIconButton,
    HiddenFileInput,
    ErrorMessage
} from './styled';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';
import { ReviewButtons } from 'src/pages/Quest/Review/styled';
import QuestActionButton from '../ReviewButton';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { postRequestModifications } from 'src/redux/quests/service';
import { setSnackbarAttributes } from 'src/redux/snackbar/slice';
import { ReactComponent as AttachmentIcon } from 'src/assets/icons/attachment.svg';
import useFileUploadWithValidation, {
    UploadedFile
} from 'src/hooks/useFileUploadWithValidation';
import { generateUniqueId } from 'src/utils/attachments';
import AttachmentItem from 'src/shared/AttachmentItem';
import UploadProgress from 'src/shared/UploadProgress';

interface RequestModificationProps {
    questId: string;
    onSuccess: () => void;
    onCancel: () => void;
}

const RequestModification: FC<RequestModificationProps> = ({
    questId,
    onSuccess,
    onCancel
}) => {
    const dispatch = useAppDispatch();

    const [description, setDescription] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFilesSelect = useFileUploadWithValidation({
        onFilesUpdate: setFiles,
        onError: setFileError
    });

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const selectedFilesWithIds = Array.from(e.target.files).map(
                    (file) => ({
                        file,
                        id: generateUniqueId()
                    })
                );
                handleFilesSelect(selectedFilesWithIds);
            }
            // Reset the input so the same file can be selected again
            if (e.target) {
                e.target.value = '';
            }
        },
        [handleFilesSelect]
    );

    const handleAttachmentClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const removeAttachment = useCallback((idToRemove: number): void => {
        setFiles((prevFiles) =>
            prevFiles.filter((file) => {
                // Match by fileId if it exists, otherwise match by local id
                if (file.fileId) {
                    return file.fileId !== idToRemove;
                }
                return file.id !== idToRemove;
            })
        );
    }, []);

    const cancelUpload = useCallback(
        (fileId: number): void => {
            const fileToCancel = files.find((f) => f.id === fileId);

            if (fileToCancel && fileToCancel.uploadController) {
                fileToCancel.uploadController.abort();
            }

            removeAttachment(fileId);
        },
        [files, removeAttachment]
    );

    const handleEditorChange = useCallback((value: string | undefined) => {
        setDescription(value || '');
    }, []);

    const onSubmit = useCallback(() => {
        // Check if any files are currently uploading
        const hasUploadingFiles = files.some(
            (file) => file.status === 'uploading'
        );
        if (hasUploadingFiles) {
            setFileError(
                'Please wait for all uploads to complete before submitting'
            );
            return;
        }

        setIsLoading(true);

        const attachmentIds = files
            .filter((f) => f.fileId && f.status === 'completed')
            .map((f) => Number(f.fileId));

        dispatch(
            postRequestModifications({
                questId,
                params: {
                    feedback: description,
                    attachmentList: attachmentIds
                }
            })
        )
            .unwrap()
            .then(() => {
                dispatch(
                    setSnackbarAttributes({
                        snackbarOpen: true,
                        snackbarType: 'success',
                        snackbarMessage:
                            'Modification request submitted successfully.'
                    })
                );
                onSuccess();
            })
            .catch(() => {
                dispatch(
                    setSnackbarAttributes({
                        snackbarOpen: true,
                        snackbarType: 'error',
                        snackbarMessage:
                            'An error occurred while submitting your feedback. Please try again.'
                    })
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [dispatch, questId, description, files, onSuccess]);

    const isDescriptionValid = description.trim().length > 0;
    const hasUploadingFiles = files.some((file) => file.status === 'uploading');

    return (
        <Container>
            <Title>Request Modification</Title>
            <Subtitle>
                You can add additional details in addition to the PR comments
            </Subtitle>

            <RichTextEditor>
                <MarkdownEditor
                    value={description}
                    onChange={handleEditorChange}
                    height={300}
                />
            </RichTextEditor>

            <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                multiple
                onChange={handleFileChange}
            />

            <AttachmentsContainer>
                <AttachmentIconButton onClick={handleAttachmentClick}>
                    <AttachmentIcon color={color.lightningYellow} />
                </AttachmentIconButton>
                {files
                    .filter((file) => file.status === 'completed')
                    .map((attachedFile) => (
                        <AttachmentItem
                            key={`attachment-${attachedFile.id}`}
                            file={{
                                id:
                                    Number(attachedFile.fileId) ||
                                    generateUniqueId(),
                                name: attachedFile.file.name,
                                type: attachedFile.file.type
                            }}
                            index={Number(attachedFile.fileId)}
                            removeAttachment={removeAttachment}
                        />
                    ))}
            </AttachmentsContainer>

            <UploadProgress files={files} onCancelUpload={cancelUpload} />

            {fileError && <ErrorMessage>{fileError}</ErrorMessage>}

            <ReviewButtons>
                <QuestActionButton
                    label="Cancel"
                    onClick={onCancel}
                    width="150px"
                    aria-label="Cancel the modification request"
                />
                <QuestActionButton
                    label="Save"
                    onClick={onSubmit}
                    width="150px"
                    aria-label="Save the modification request"
                    disabled={!isDescriptionValid || hasUploadingFiles}
                    isLoading={isLoading}
                />
            </ReviewButtons>
        </Container>
    );
};

export default RequestModification;
